import { Alert, Button, Grid, InputAdornment, LinearProgress, MenuItem, OutlinedInput, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/userContext';
import { CuotasType, useGetCuotas, useGetdescuento, useSetCuotas, useSetdescuento } from '../../hooks/cuotas';
import { useGetGrupos } from '../../hooks/useGrupos';
import hash from 'object-hash';

// Tipo para cada celda
type CeldaMonto = {
  efectivo: number | null;
  transferencia: number | null;
};

export function CuotasMensuales() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useContext(UserContext);

  const currentYear = dayjs().year();
  const currentMonth = dayjs().month();

  const [values, setValues] = useState<{ [key: number]: (CeldaMonto | null)[] }>();
  const [originalValues, setOriginalValues] = useState<{ [key: number]: (CeldaMonto | null)[] }>();


  const [grupoSeleccionado, setGrupoSeleccionado] = useState<string>('');
  const [mutableCuotas, setMutableCuotas] = useState<CuotasType[]>();
  const [mutableDescuento, setMutableDescuento] = useState<{ value: number }>();

  useSetCuotas(mutableCuotas, user);
  const setDescuento = useSetdescuento(mutableDescuento, user);
  const cuotas = useGetCuotas(user);
  const grupos = useGetGrupos(user);
  const descuento = useGetdescuento(user);

  useEffect(() => {
    setTimeout(() => cuotas.refetch(), 500);
  }, [mutableCuotas]);

  useEffect(() => {
    if (grupos.data) {
      setGrupoSeleccionado(grupos.data[0].nombre);
    }
  }, [grupos.data]);

  useEffect(() => {
    if (descuento.data) {
      setMutableDescuento({ value: descuento.data.porcentaje });
    }
  }, [descuento.data]);

  const monthsHeader = [
    '',
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  const cutotasFiltradas = React.useMemo(() => cuotas?.data?.filter((c) => c.grupo === grupoSeleccionado), [grupoSeleccionado, cuotas.data]);

  const olderRecord = cutotasFiltradas?.length ? cutotasFiltradas.reduce((prev, curr) => {
    return (curr.ano < prev.ano) ? curr : prev;
  }).ano : dayjs().year();

  const listingYears:(number | never)[] = [];
  for (let year = olderRecord; year <= currentYear + 1; year++) listingYears.push(year);

  // Fill values

  const fillValuesWithLoadedData = () => {
    if (!cutotasFiltradas) return;
    const mutableValues: { [key: number]: (CeldaMonto | null)[] } = {};
    for (const y of listingYears) {
      mutableValues[y] = [];
      for (let mes = 1; mes <= 12; mes++) {
        const cuota = cutotasFiltradas.find((c) => c.ano === y && c.mes === mes);
        if (cuota) {
          mutableValues[y].push({
            efectivo: (cuota as any).montoEfectivo ?? cuota.monto ?? null,
            transferencia: (cuota as any).montoTransferencia ?? null,
          });
        } else {
          mutableValues[y].push(null);
        }
      }
    }
    setOriginalValues(mutableValues);
    setValues(mutableValues);
  };

  useEffect(() => {
    fillValuesWithLoadedData();
  }, [cutotasFiltradas, grupos.data, grupoSeleccionado]);

  const handleSubmit = () => {
    if (!cutotasFiltradas || !values) return;
    const allCuotas: CuotasType[] = [];
    for (const year in values) {
      values[year].forEach((celda, index) => {
        const mes = index + 1;
        if (!celda) return;
        const { efectivo, transferencia } = celda;
        if (efectivo == null || transferencia == null) return;

        const existe = cutotasFiltradas.find(
          (c) => c.ano === Number(year) && c.mes === mes && c.grupo === grupoSeleccionado,
        );
        const modificada =
          existe?.montoEfectivo !== efectivo ||
          existe?.montoTransferencia !== transferencia;

        if (!existe || modificada) {
          allCuotas.push({
            ano: Number(year),
            mes,
            montoEfectivo: efectivo,
            montoTransferencia: transferencia,
            grupo: grupoSeleccionado,
          });
        }
      });
    }
    setMutableCuotas(allCuotas);
  };

  const celdaActual = values?.[currentYear]?.[currentMonth];
  const celdaSiguiente = values?.[currentYear]?.[currentMonth + 1];
  const faltanCuotas =
    !celdaActual?.efectivo || !celdaActual?.transferencia ||
    !celdaSiguiente?.efectivo || !celdaSiguiente?.transferencia;

  if (!user?._id) return null;
  if (!cuotas.data || cuotas.isLoading || !grupos.data || grupos.isLoading || !values)
    return <LinearProgress />;

  return (
    <>
      <Grid container marginBottom={2} spacing={2} style={{ width: '100%', maxWidth: '100%', flexWrap: 'wrap' }}>
        <Grid
          item
          xs={12}
          md={8}
          padding={2}
          style={{
            justifyContent:'center',
            alignItems:'flex-end',
          }}
        >
          <Paper elevation={10}  style={{ height: 130, width: '100%', padding: 8 }}>
            <Grid container spacing={2} >
              <Grid item xs={12}>
                <Typography color='primary' fontWeight='bold' >Configurar cuotas de grupo:</Typography>
              </Grid>
              <Grid item xs={12}>
                <Select
                  input={<OutlinedInput sx={{ fontSize: 18, fontWeight: 'bold' }} />}
                  id="selector-grupo-asistencia"
                  value={grupoSeleccionado}
                  onChange={(event: SelectChangeEvent) => setGrupoSeleccionado(event.target.value)}
                  margin='dense'
                >
                  {grupos.data.map((grupo) => (
                    <MenuItem key={grupo._id} value={grupo.nombre}>{grupo.nombre}</MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          style={{
            justifyContent:'center',
            alignItems:'flex-end',
          }}
          padding={2}
          paddingRight={0}
        >
          <Paper elevation={10}  style={{ height: 130, width: '100%', padding: 8 }}>
            <Grid
              container
              spacing={2}
              direction="row"
              justifyContent="center"
              alignItems="flex-end"
            >
              <Grid item xs={12}>
                <Typography color='primary' fontWeight='bold' >Descuento por hermano</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="dense"
                  id={'descuento'}
                  type="number"
                  variant="standard"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setMutableDescuento({ value: Number(event.target.value) });
                  }}
                  value={mutableDescuento?.value ?? ''}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant='contained'
                  size="small"
                  onClick={() => {
                    setDescuento.refetch();
                    setTimeout(() => descuento.refetch(), 500);
                  }}
                >
          Cambiar
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <TableContainer
        component={Paper}
        elevation={10}
        style={{ position: 'relative',
          padding: 16,
          height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 285px)',
          overflowX: 'auto' }}
      >
        <Table sx={{ minWidth: 800 }} aria-label="simple table" >
          <TableBody>
            <TableRow key="months" >
              {monthsHeader?.map((month) => (<TableCell key={`${month}-table-header`} style={{ borderStyle: 'solid', padding: 4, minWidth: 160 }}>{month}</TableCell>))}
            </TableRow>
            {listingYears?.map((year) => (
              <TableRow
                key={`${year}-table-header`}
                style={{ borderStyle: 'solid' }}
              >
                <TableCell style={{ borderStyle: 'solid', padding: 4, minWidth: 160 }}>{year}</TableCell>
                {values[year]?.map((celda, mes) => {
                  const twoDigitsMonth = String(mes + 1).length < 2 ? String(mes + 1).padStart(2, '0') : String(mes + 1);
                  const esPasado = dayjs(`01-${twoDigitsMonth}-${year}`, 'DD-MM-YYYY').isBefore(dayjs(), 'days');
                  const esFuturo = dayjs(`01-${twoDigitsMonth}-${year}`, 'DD-MM-YYYY').isAfter(dayjs().add(2, 'months'), 'days');
                  let color = 'rgb(200 200 200)'; // disable
                  if (!esPasado && celda) color = 'rgb(150 213 179)';
                  if (!celda) color = 'rgb(220 165 165)';
                  if (esFuturo && !celda) color = '#d2e0ef';

                  return (
                    <TableCell  key={`table-cell-${year}-${twoDigitsMonth}`} style={{ borderStyle: 'solid', backgroundColor: color, padding: 4 }}>
                      <Grid container spacing={0.5}>
                        <Grid item xs={6}>
                          <TextField
                            key={`efectivo-${year}-${twoDigitsMonth}`}
                            type="number"
                            fullWidth
                            variant="standard"
                            placeholder="Efect."
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><span style={{ fontSize: 10 }}>$</span></InputAdornment>,
                              style: { fontSize: 13 },
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setValues((prev) => {
                                const next = JSON.parse(JSON.stringify(prev ?? {}));
                                next[year][mes] = {
                                  efectivo: e.target.value ? Number(e.target.value) : null,
                                  transferencia: next[year][mes]?.transferencia ?? null,
                                };
                                return next;
                              });
                            }}
                            value={celda?.efectivo ?? ''}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            key={`transf-${year}-${twoDigitsMonth}`}
                            type="number"
                            fullWidth
                            variant="standard"
                            placeholder="Transf."
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><span style={{ fontSize: 10 }}>$</span></InputAdornment>,
                              style: { fontSize: 13 },
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setValues((prev) => {
                                const next = JSON.parse(JSON.stringify(prev ?? {}));
                                next[year][mes] = {
                                  efectivo: next[year][mes]?.efectivo ?? null,
                                  transferencia: e.target.value ? Number(e.target.value) : null,
                                };
                                return next;
                              });
                            }}
                            value={celda?.transferencia ?? ''}
                          />
                        </Grid>
                      </Grid>
                    </TableCell>
                  );
                },
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div style={{ position: 'absolute', bottom: 10, right: 10, width: '100%', paddingLeft: 24 }}>
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
            >
              {faltanCuotas &&
              <Alert severity="error" style={{ width: '100%', textAlign: 'left' }}>
                  Faltan cargar los valores de las cuotas de este mes o el mes próximo!
              </Alert>
              }
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={6}
              style={{
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                disabled={hash(originalValues ?? {}) === hash(values)}
                variant='contained'
                onClick={handleSubmit}
                style={{ marginRight: 10 }}
              >
                Guardar
              </Button>
              <Button
                disabled={hash(originalValues ?? {}) === hash(values)}
                variant='contained'
                onClick={() => fillValuesWithLoadedData() }
              >
                Descartar
              </Button>
            </Grid>
          </Grid>
        </div>
      </TableContainer>
    </>
  );
}
